import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { SiteSummary } from 'app/_models/site-summary.model';
import { ContractorService } from 'app/_services/contractor.service';
import { SessionsService } from 'app/_services/sessions.service';
import { SiteService } from 'app/_services/site.service';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { environment } from 'environments/environment';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import Swal from 'sweetalert2';
import { SessionPhotoModalComponent } from '../session-photo-modal/session-photo-modal.component';
import { SiteFormModalComponent } from './site-form-modal/site-form-modal.component';
import { NotificationService } from 'app/_services/notification.service';
import { PlausibleAnalyticsService } from 'app/_services/plausible-analytics.service';
import { throwError, from, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, toArray } from 'rxjs/operators';

@Component({
  selector: 'app-worker-sessions',
  templateUrl: './worker-sessions.component.html',
})
export class WorkerSessionsComponent implements OnInit {
  public pageContext = 'worker-sessions';
  public routeRole;
  private isSiteOwnerView: any;
  public isSiteDelegateView: any;
  public showContractors: boolean;
  public showWorkers: boolean;
  public progressValue: number;
  public inProgress: boolean = false;
  public showExportButton: boolean = true;

  // date filters
  public toDate: Date;
  public fromDate: Date;

  // dropdown filters
  public sitesArray: any[] = [];
  public companiesArray: any[] = [];
  public workersArray: any[] = [];

  public selectedSite: any = { label: 'Select Site', value: undefined };
  public selectedCompany: any = { label: 'Select Company', value: undefined };
  public selectedWorker: any = { label: 'Select Worker', value: undefined };
  public selectedSessionType: any = { label: 'All', value: undefined };


  public sessionTypes: any[] = [
    { label: 'All', value: undefined },
    { label: 'Site Session Only', value: 'SITE' },
    { label: 'Work Order Session Only', value: 'WORK_ORDER' }
  ];


  // table data
  public workerSessions: any[] = [];
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator;
  private exportSubscription: Subscription;
  private readonly exportPageSize = 200;
  private readonly exportConcurrency = 4;

  // Export Options
  private options = {
    title: '',
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalseparator: '.',
    showLabels: true,
    showTitle: false,
    useBom: true, // Excel-friendly UTF-8
    noDownload: false,
    headers: [
      'ID',
      'Site Name',
      'Worker',
      'Company',
      'Work Order ID',
      'Work Order',
      'Asset',
      'Reason',
      'Start Date',
      'Start Time',
      'End Date',
      'End Time',
      'Duration',
      'Site Session ID',
      'No. Guests',
      'Notes',
      'Has Photos',
      'Comments',
      'Site Form Responses',
    ],
  };

  constructor(
    private modalService: NgbModal,
    private sessionsService: SessionsService,
    private contractorService: ContractorService,
    private sitesService: SiteService,
    private globals: Globals,
    private notificationService: NotificationService,
    private plausibleService: PlausibleAnalyticsService
  ) {
    this.routeRole = globals.user.routeRole;
    this.isSiteOwnerView = this.routeRole === 'site-owner'
    this.isSiteDelegateView = this.routeRole === 'site-delegate'
  }

  ngOnInit() {
    this.toDate = DateTimeUtils.timelessDateAsUTCDate(new Date);
    const tempFromDate = new Date().setDate(this.toDate.getDate() - 30);
    this.fromDate = new Date(tempFromDate);

    this.resetPagination();
    this.populateDataTable();
    this.getSitesForDropdownFilter();

    if (!this.isSiteDelegateView) {
      this.getCompaniesForDropdownFilter();
      this.showContractors = true;
      this.showWorkers = false;
    }
  }

  // -- Shared service call
  private getWorkerSessionsRequest(pagination, fromTimestamp: number, toTimestamp: number, useLatestOnlyHeader: boolean = false, includeComments: boolean = false) {
    return this.sessionsService.getAllWorkerSessions(
      pagination,
      'startTime,asc',
      fromTimestamp,
      toTimestamp,
      this.selectedSite.value,
      this.selectedCompany.value,
      this.selectedWorker.value,
      this.selectedSessionType.value,
      useLatestOnlyHeader,
      includeComments
    );
  }

  // -- Filter set up
  public getSitesForDropdownFilter() {
    this.sitesService.getSiteListSummary().subscribe((data: SiteSummary[]) => {
      this.sitesArray.push(this.selectedSite);

      data.forEach((element: SiteSummary) => {
        const item = {
          label: element.siteName,
          value: element.siteId,
          siteOwnerId: element.siteOwnerSkylarkUserId,
        };
        this.sitesArray.push(item);
      });
    });
  }

  public getCompaniesForDropdownFilter() {

    if (this.isSiteOwnerView || this.isSiteDelegateView) {
      let siteOwnerId;

      if (this.isSiteOwnerView) {
         siteOwnerId = this.globals.siteOwnerUsers[0].skylarkUserId;
      } else if (this.isSiteDelegateView) {
         siteOwnerId = this.selectedSite.siteOwnerId;
      }

      this.contractorService.getLinkedContractors(siteOwnerId, ({ size: 999, page: 0 }), 'name,asc', undefined).subscribe(
        (data: any) => {
          this.companiesArray = [
            { label: 'Select a company', value: undefined },
            ...data.body.map((element) => ({
              label: element.name,
              value: JSON.stringify(element.id),
            }))];
        },
        (error) => {
          console.error(error);
          this.notificationService.notify('danger', 'Error retrieving contractor list');
        });
    } else {
      this.contractorService.getAllContractorFullList().subscribe((data: any) => {
        this.companiesArray.push(this.selectedCompany);
        data.forEach((element) => {
          const item = {
            label: element.name,
            value: JSON.stringify(element.id),
          };
          this.companiesArray.push(item);
        });
      });
    }
  }

  public getAllWorkersInSelectedCompanyForDropdownFilter() {
    const newList = [this.selectedWorker];

    if (this.selectedCompany.value) {
        this.contractorService.getWorkerListForContractor(this.selectedCompany.value, true).subscribe(
        (data: any) => {
          const hasWorkers = data
          if (hasWorkers.length === 0) {
            this.notificationService.notify('danger', 'No workers are currently set up for this contractor.');
            this.showWorkers = false;
          } else {
            data.forEach((element) => {
              const item = {
                label: element.firstName + ' ' + element.lastName,
                value: JSON.stringify(element.skylarkUserId),
              };
              newList.push(item);
            });
            this.workersArray = newList;
            this.showWorkers = true;
          }
        },
        (error) => {
          console.error(error);
          this.notificationService.notify('danger', 'Error retrieving workers list');
        });
    }
  }

  // -- Filters
  public onFromDateChange(event) {
    if (this.inProgress) {
      return;
    }

    if (!event) { return; }

    this.fromDate = event;
    this.goToFirstPage();
    this.populateDataTable();
  }

  public onToDateChange(event) {
    if (this.inProgress) {
      return;
    }

    if (!event) { return; }

    this.toDate = event;
    this.goToFirstPage();
    this.populateDataTable();
  }

  public onSelectedSiteChange(event) {
    if (this.inProgress) {
      return;
    }

    this.selectedSite = event;

    if (this.isSiteDelegateView) {
      this.clearContractorDropdown();
      this.clearWorkersDropdown();

      if (this.selectedSite.siteOwnerId === undefined) {
        this.showContractors = false;
        this.showWorkers = false;
      } else {
        this.showContractors = true;
        this.showWorkers = true;
        this.getCompaniesForDropdownFilter();
      }
    }

    this.goToFirstPage();
    this.populateDataTable();
  }

  public onSelectedCompanyChange(event) {
    if (this.inProgress) {
      return;
    }

    this.selectedCompany = event;

    // reset worker dropdown
    this.clearWorkersDropdown();
    this.getAllWorkersInSelectedCompanyForDropdownFilter();

    this.goToFirstPage();
    this.populateDataTable();
  }

  public onSelectedWorkerChange(event) {
    if (this.inProgress) {
      return;
    }

    this.selectedWorker = event;
    this.goToFirstPage();
    this.populateDataTable();
  }

  public onSessionTypeChanged(event) {
    if (this.inProgress) {
      return;
    }

    this.goToFirstPage();
    this.populateDataTable();
  }

  private clearContractorDropdown() {
    this.selectedCompany = { label: 'Select Company', value: undefined };

    this.companiesArray = [];
  }

  private clearWorkersDropdown() {
    this.selectedWorker = { label: 'Select Worker', value: undefined };

    this.workersArray = [];
  }

  // -- Table actions
  public viewNotes(notes) {
    Swal.fire({
      title: 'Session notes',
      text: notes,
      type: 'info'
    });
  }

  public viewPhotos(sessionId) {
    const modalRef = this.modalService.open(SessionPhotoModalComponent, { size: 'lg' });
    modalRef.componentInstance.sessionId = sessionId;
  }

  public viewSiteForms(session) {
    const modalRef = this.modalService.open(SiteFormModalComponent, { size: 'lg' });
    modalRef.componentInstance.sessionId = session.id;
    modalRef.componentInstance.siteOwnerLogin = session.siteSession.site.siteOwner.login;
  }

  // -- Table controls
  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
  }

  public paginate(event) {
    if (this.inProgress) {
      return;
    }

    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.populateDataTable();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public cancelExport() {
    if (!this.inProgress) {
      return;
    }

    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
      this.exportSubscription = undefined;
    }

    this.resetExportState();
    this.notificationService.notify('info', 'Export cancelled');
  }

  private resetExportState() {
    this.progressValue = 0;
    this.inProgress = false;
    this.showExportButton = true;
    this.exportSubscription = undefined;
  }

  // -- Page display
  private populateDataTable() {
    if (this.inProgress) {
      return;
    }

    const fromTimestamp = this.fromDate.getTime();
    const toTimestamp = this.toDate.getTime();

    this.getWorkerSessionsRequest(this.pagination, fromTimestamp, toTimestamp, true).subscribe((response: any) => {
      this.workerSessions = [];

      if (!response || !response.body) {
        console.error('response empty - too large a set or not fast enough connection');
        this.notificationService.notify('danger', 'Response took too long - try a smaller data set');
      } else {
        response.body.forEach((element) => {
          this.workerSessions.push(element);
        });
      }

      this.pagination.totalRecords = Number(response.headers.get('X-Total-Count'));
    });
  }

  // -- Export
  public export() {
    if (this.inProgress) {
      return;
    }

    this.trackButtonClick('btn-export-csv');

    const fromTimestamp = this.fromDate.getTime();
    const toTimestamp = this.toDate.getTime();
    this.progressValue = 0;
    this.inProgress = true;
    this.showExportButton = false;
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }

    setTimeout(() => {
      this.progressValue = 10;
    }, 0);

    this.exportSubscription = this.probeForTotalRecords(fromTimestamp, toTimestamp).pipe(
      switchMap(totalRecords => {
        const totalPages = this.calculateTotalPages(totalRecords);

        if (totalPages < 1) {
          this.notificationService.notify('info', 'No worker sessions found for export.');
          return throwError(new Error('No worker sessions found.'));
        }

        return this.executeExportRequests(totalPages, fromTimestamp, toTimestamp);
      })
    ).subscribe({
      next: (exportedSessions) => {
        this.exportData(exportedSessions);
        this.resetExportState();
      },
      error: (error) => {
        if (error && error.message) {
          this.notificationService.notify('danger', 'Export failed - ' + error.message);
        } else {
          this.notificationService.notify('danger', 'Export cancelled');
        }
        this.resetExportState();
      }
    });
  }

  private calculateTotalPages(totalRecords: number): number {
    if (!totalRecords) {
      return 0;
    }
    return Math.max(1, Math.ceil(totalRecords / this.exportPageSize));
  }

  private probeForTotalRecords(fromTimestamp: number, toTimestamp: number) {
    return this.getWorkerSessionsRequest({ size: 1, page: 0 }, fromTimestamp, toTimestamp).pipe(
      map(response => {
        if (!response || !response.headers) {
          throw new Error('Unable to determine export size.');
        }
        const totalCount = Number(response.headers.get('X-Total-Count')) || 0;
        if (!totalCount) {
          throw new Error('No worker sessions found.');
        }
        return totalCount;
      }),
      catchError(error => {
        console.error(error);
        return throwError(error);
      })
    );
  }

  private executeExportRequests(totalPages: number, fromTimestamp: number, toTimestamp: number) {
    if (!totalPages || totalPages < 1) {
      return throwError(new Error('No worker sessions found.'));
    }

    const workerSessionsByPage: any[][] = new Array(totalPages);

    const pageRequests = Array.from({ length: totalPages }, (_value, index) => {
      return this.getWorkerSessionsRequest({ size: this.exportPageSize, page: index }, fromTimestamp, toTimestamp, false, true).pipe(
        tap(response => {
          if (!response || !response.body || !response.body.length) {
            throw new Error('Worker sessions export failed. Please retry.');
          }

          workerSessionsByPage[index] = response.body;
          this.progressValue = Math.min(99, this.progressValue + (100 / totalPages));
        }),
        catchError(error => {
          console.error(error);
          this.notificationService.notify('danger', 'Export failed - ' + error.message);
          return throwError(error);
        })
      );
    });

    return from(pageRequests).pipe(
      mergeMap(pageRequest => pageRequest, this.exportConcurrency),
      toArray(),
      map(() => workerSessionsByPage.reduce((acc, pageRows) => acc.concat(pageRows || []), []))
    );
  }

  private exportData(selectedSessions) {
    const sessionsToExport = [];
    let comments = '';
    selectedSessions.forEach((element) => {
      const sessionObj = {
        id: element.id,
        siteName: element.siteSession.site.name,
        worker: element.user.firstName + ' ' + element.user.lastName,
        company: element.user.workerUser.maintenance.name,
        workOrderId: '',
        workOrder: '',
        asset: '',
        reason: element.reason,
        startDate: new Date(element.startTime).toLocaleDateString('en-GB'),
        startTime: new Date(element.startTime).toLocaleTimeString('en-GB'),
        endDate: '',
        endTime: '',
        duration: '',
        siteSessionId: element.siteSession.id,
        guests: element.guestCount,
        notes: element.notes ? element.notes : '',
        hasPhotos: '',
        comments: '',
        siteFormResponseCount: element.siteFormResponseCount,
      };
      if (element.siteSession.workOrder === null) {
        sessionObj.workOrder = 'Site Session';
        sessionObj.asset = 'n/a';
      } else {
        sessionObj.workOrderId = element.siteSession.workOrder.id;
        sessionObj.workOrder = element.siteSession.workOrder.description;
        sessionObj.asset = element.siteSession.workOrder.asset.name;

        comments = '';
        if (element.siteSession.workOrder) {
          element.siteSession.workOrder.workOrderComments
            .sort((a, b) => {
              return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
            })
            .forEach(comment => {
              comments += `${new Date(comment.createdDate).toLocaleString('en-GB')} --- ${comment.createdBy} --- ${comment.comment} \n`;
            });
        }
        sessionObj.comments = comments;
      }
      if (element.endTime === null) {
        sessionObj.endDate = 'In Progress';
        sessionObj.endTime = 'In Progress';
      } else {
        sessionObj.endDate = new Date(element.endTime).toLocaleDateString('en-GB');
        sessionObj.endTime = new Date(element.endTime).toLocaleTimeString('en-GB');
        sessionObj.duration = DateTimeUtils.durationAsHoursMinutesSecondsString(new Date(element.startTime), new Date(element.endTime));
      }
      if (element.siteSession.uploadCount > 0) {
        sessionObj.hasPhotos = 'Yes';
      }
      sessionsToExport.push(sessionObj);
    });

    const fileName = 'WorkerSessionsReport_' + DateTimeUtils.localDateToTimelessString(this.fromDate) + '_to_' + DateTimeUtils.localDateToTimelessString(this.toDate);
    // tslint:disable-next-line:no-unused-expression
    new ngxCsv(sessionsToExport, fileName, this.options);
  }

  // -- Plausible
  public trackButtonClick(buttonName: string) {
    const eventName = `${buttonName}_${this.pageContext}`;
    this.plausibleService.trackButtonClick(eventName);
  }

}
